import {offlineDb} from './db';
import {
  DependsOnFailedItemError,
  DeterministicExecutorError,
  registerOutboxExecutor,
} from './executorRegistry';
import type {OutboxExecutor, OutboxExecutorContext} from './executorRegistry';
import {presignAndUploadBlob} from './fileUpload';
import {deleteSessionFile, findSessionFile} from './files';
import {
  addOutboxItem,
  discardItems,
  PENDING_STATUSES,
  RETRY_RESET_FIELDS,
} from './outbox';
import type {OutboxItem} from './types';

/** Outbox kind of a queued blob upload, shared by every offline domain. */
export const FILE_UPLOAD_KIND = 'file.upload';

// Kind used before the upload helpers were shared; a device can still hold
// queued rows under it, so both kinds are matched and executed.
const LEGACY_FILE_UPLOAD_KIND = 'form.uploadFile';

export const FILE_UPLOAD_KINDS: readonly string[] = [
  FILE_UPLOAD_KIND,
  LEGACY_FILE_UPLOAD_KIND,
];

export const isFileUploadKind = (kind: string): boolean =>
  FILE_UPLOAD_KINDS.includes(kind);

// A pending offline capture carried in a payload's JSON until sync rewrites it
// with the real upload url. Superset of IFormDocumentUploadFile.
export interface OfflineUploadPlaceholder {
  name: string;
  value: string;
  encodedUrl: string;
  encodedKey: string;
  success: boolean;
  type?: string;
  pending?: boolean;
  refKey?: string;
}

export interface PendingUploadRef {
  refKey: string;
  filename: string;
  type?: string;
}

/** Parses a value into its upload-file array, or undefined if it isn't one. */
export const parseUploadFiles = (
  value: unknown
): OfflineUploadPlaceholder[] | undefined => {
  if (typeof value !== 'string') return undefined;
  try {
    const parsed = JSON.parse(value);
    if (
      Array.isArray(parsed) &&
      parsed.every(item => item && typeof item.name === 'string')
    ) {
      return parsed as OfflineUploadPlaceholder[];
    }
  } catch {
    /* not an upload-file value */
  }
  return undefined;
};

/** Every offline-captured pending placeholder across a set of values. */
export const collectPendingRefs = (
  values: Iterable<unknown>
): PendingUploadRef[] => {
  const refs: PendingUploadRef[] = [];
  for (const value of values) {
    parseUploadFiles(value)
      ?.filter(file => file.pending && file.refKey)
      .forEach(file =>
        refs.push({refKey: file.refKey!, filename: file.name, type: file.type})
      );
  }
  return refs;
};

export const hasPendingUploads = (values: Iterable<unknown>): boolean =>
  collectPendingRefs(values).length > 0;

// Enqueues one upload item per pending capture, idempotent per refKey so a
// re-submit (e.g. after a validation retry) never double-queues an upload.
export const enqueuePendingUploads = async (
  sessionId: string,
  values: Iterable<unknown>
): Promise<void> => {
  const refs = collectPendingRefs(values);
  if (refs.length === 0) return;

  const existing = await offlineDb.outbox
    .where('sessionId')
    .equals(sessionId)
    .filter(item => isFileUploadKind(item.kind))
    .toArray();
  const enqueuedRefKeys = new Set(
    existing.map(item => (item.payload as PendingUploadRef).refKey)
  );

  // Sequential: each row's seq is read from the rows already written here.
  for (const ref of refs.filter(item => !enqueuedRefKeys.has(item.refKey))) {
    await addOutboxItem({sessionId, kind: FILE_UPLOAD_KIND, payload: ref});
  }
};

// Presigns and uploads one captured blob, mapping refKey → {accessUrl, key} so
// the owning create executor can rewrite the placeholder before submitting.
export const uploadFileExecutor: OutboxExecutor = async (
  payload,
  _clientSubmissionId,
  ctx
) => {
  const {refKey, filename, type} = payload as PendingUploadRef;

  const file = await findSessionFile(ctx.sessionId, refKey);
  if (!file) {
    throw new DeterministicExecutorError(
      `No captured file found for refKey "${refKey}".`
    );
  }

  const {accessUrl, key} = await presignAndUploadBlob({
    filename,
    presignContentType: type ?? file.contentType,
    blob: file.blob,
    uploadContentType: file.contentType,
    heartbeat: ctx.heartbeat,
  });

  await ctx.recordIdMapping(refKey, JSON.stringify({accessUrl, key}));
  await deleteSessionFile(ctx.sessionId, refKey);
  return {summary: `Uploaded ${filename}`};
};

export interface FailedOutboxItem {
  item: OutboxItem;
  /** Message to show the user — the attachment error when the parent only
   * failed because one of its uploads did. */
  error: string;
}

export interface PendingOutboxItems {
  /** Parent items still queued, in flight, or awaiting an automatic retry. */
  pendingCount: number;
  /** Parent items that stopped on an error and need the user to act. */
  failedItems: FailedOutboxItem[];
}

export interface OutboxCascadeConfig {
  /** Outbox kind of the parent item the uploads were queued with. */
  kind: string;
  /** The pending upload refs a parent payload still carries. */
  extractRefs: (payload: unknown) => PendingUploadRef[];
}

export interface OutboxCascade {
  retry: (item: OutboxItem) => Promise<void>;
  discard: (item: OutboxItem) => Promise<void>;
  pendingItems: (sessionId: string) => Promise<PendingOutboxItems>;
}

export const EMPTY_PENDING_ITEMS: PendingOutboxItems = {
  pendingCount: 0,
  failedItems: [],
};

const isDeterministicFailure = (item: OutboxItem): boolean =>
  item.status === 'failed' && item.failureKind === 'deterministic';

/**
 * Treats a queued parent item and the uploads enqueued with it as one unit of
 * work: retry, discard, and pending/failed reporting cascade to its uploads.
 */
export const createOutboxCascade = ({
  kind,
  extractRefs,
}: OutboxCascadeConfig): OutboxCascade => {
  const refKeys = (item: OutboxItem): Set<string> =>
    new Set(extractRefs(item.payload).map(ref => ref.refKey));

  const uploadSiblings = async (
    item: OutboxItem,
    keys: Set<string>
  ): Promise<OutboxItem[]> => {
    if (keys.size === 0) return [];
    const siblings = await offlineDb.outbox
      .where('sessionId')
      .equals(item.sessionId)
      .toArray();
    return siblings.filter(
      sibling =>
        isFileUploadKind(sibling.kind) &&
        keys.has((sibling.payload as PendingUploadRef).refKey)
    );
  };

  // A parent cannot succeed while one of its uploads is permanently failed, so
  // that upload's error is the one the user must act on.
  const failureError = (item: OutboxItem, uploads: OutboxItem[]): string => {
    const keys = refKeys(item);
    const blocking = uploads.find(
      upload =>
        keys.has((upload.payload as PendingUploadRef).refKey) &&
        isDeterministicFailure(upload)
    );
    if (blocking) {
      const {filename} = blocking.payload as PendingUploadRef;
      return `Attachment "${filename}": ${blocking.error ?? 'Upload failed.'}`;
    }
    return item.error ?? 'Sync failed.';
  };

  return {
    retry: item =>
      offlineDb.transaction('rw', offlineDb.outbox, async () => {
        const current = await offlineDb.outbox.get(item.id);
        if (!current || current.status !== 'failed') {
          throw new Error('Only failed items can be retried.');
        }
        const siblings = await uploadSiblings(item, refKeys(item));
        for (const sibling of siblings) {
          if (sibling.status !== 'failed') continue;
          await offlineDb.outbox.update(sibling.id, RETRY_RESET_FIELDS);
        }
        await offlineDb.outbox.update(item.id, RETRY_RESET_FIELDS);
      }),

    discard: async item => {
      await offlineDb.transaction(
        'rw',
        [offlineDb.outbox, offlineDb.files],
        async () => {
          const keys = refKeys(item);
          const siblings = await uploadSiblings(item, keys);
          const ids = siblings
            .filter(sibling => sibling.status !== 'succeeded')
            .map(sibling => sibling.id);
          await Promise.all(
            [...keys].map(refKey => deleteSessionFile(item.sessionId, refKey))
          );
          await discardItems([...ids, item.id]);
        }
      );
    },

    pendingItems: async sessionId => {
      const items = await offlineDb.outbox
        .where('sessionId')
        .equals(sessionId)
        .toArray();
      const parents = items.filter(item => item.kind === kind);
      const uploads = items.filter(item => isFileUploadKind(item.kind));
      // A transient failure is retried by the sync manager, so it stays in the
      // pending count instead of asking the user to do anything.
      return {
        pendingCount: parents.filter(
          item =>
            PENDING_STATUSES.includes(item.status) &&
            !isDeterministicFailure(item)
        ).length,
        failedItems: parents
          .filter(isDeterministicFailure)
          .map(item => ({item, error: failureError(item, uploads)})),
      };
    },
  };
};

/** Registers the shared upload executor, including its legacy kind. Idempotent
 * so every offline domain can call it from its own registration. */
export const registerFileUploadExecutor = (): void => {
  FILE_UPLOAD_KINDS.forEach(kind =>
    registerOutboxExecutor(kind, uploadFileExecutor)
  );
};

/** `findParent` locator for the upload item owning `refKey`, either kind. */
export const findUploadParent =
  (sessionId: string, refKey: string): (() => Promise<OutboxItem | undefined>) =>
  async () => {
    const siblings = await offlineDb.outbox
      .where('sessionId')
      .equals(sessionId)
      .toArray();
    return siblings.find(
      sibling =>
        isFileUploadKind(sibling.kind) &&
        (sibling.payload as PendingUploadRef).refKey === refKey
    );
  };

interface ResolvedUpload {
  accessUrl: string;
  key: string;
}

const resolveUpload = (
  refKey: string,
  ctx: OutboxExecutorContext
): ResolvedUpload => {
  const mapped = ctx.resolveId(refKey);
  if (mapped === undefined) {
    // The upload sibling owning this refKey lets the sync manager tell a
    // transient parent failure from a permanent one.
    throw new DependsOnFailedItemError(
      refKey,
      findUploadParent(ctx.sessionId, refKey)
    );
  }
  return JSON.parse(String(mapped)) as ResolvedUpload;
};

// Rewrites offline placeholder attachments in an upload-file JSON value with
// their synced urls; unresolved refKeys defer via DependsOnFailedItemError.
export const rewriteUploadPlaceholders = (
  value: string,
  ctx: OutboxExecutorContext
): string => {
  const files = parseUploadFiles(value);
  if (!files?.some(file => file.pending && file.refKey)) return value;

  const rewritten = files.map(file => {
    if (!file.pending || !file.refKey) return file;
    const {accessUrl, key} = resolveUpload(file.refKey, ctx);
    return {
      ...file,
      value: accessUrl,
      encodedUrl: encodeURIComponent(accessUrl),
      encodedKey: encodeURIComponent(key),
      success: true,
      pending: undefined,
      refKey: undefined,
    };
  });
  return JSON.stringify(rewritten);
};
